import { order as orderApi } from '@squareup/sq-addons-web-sdk';
import { createLogic } from 'redux-logic';
import { queryAccounts } from 'store/api/searchCustomer';
import { CustomerConverter } from 'converters/customer-converter';

import {
  fetchCompDiscountsFulfilled,
  resetCompDiscounts,
} from 'store/compDiscounts';
import {
  addAppliedCompCard,
  deleteAppliedCompCard,
  searchCompCardsFulfilled,
  searchCompCardsRejected,
  updateAppliedCompCard,
} from 'store/searchCompCards/actions';
import { addCompDiscountRejected } from '../compDiscounts/actions';
import { simulateAccrualAndRedemption } from 'store/api/discounts/simulateAccrualAndRedemption';
import {
  SEARCH_COMP_CARDS,
  AccountQueryName,
  SYNC_APPLIED_COMP_CARD,
} from 'store/searchCompCards/types';
import { getFirstName, getLastName } from 'utils/name';
import * as customerActions from 'store/customer/actions';
import { SearchCustomersErrorCode } from 'store/api/errors';

// Extract this to a more generic place with customer search as well
const ACCOUNT_QUERY_VALIDATOR = {
  [AccountQueryName.CARD_NUMBER]: (searchValue) => {
    return (
      searchValue.length >= 2 &&
      searchValue.length <= 76 &&
      searchValue.length !== 10 &&
      /^\d+$/.test(searchValue)
    );
  },
};

// Helper function to safely get the customer, returning undefined if it fails
async function safeGetCustomer() {
  try {
    return await orderApi.getCustomer();
  } catch {
    return undefined;
  }
}

function getValidAccountQueriesList(searchValue, allowedAccountQueryNames) {
  return (
    Object.values(AccountQueryName)
      // Filter any account query names that are not valid.
      .filter(
        (accountQueryName) =>
          allowedAccountQueryNames.includes(accountQueryName) &&
          ACCOUNT_QUERY_VALIDATOR[accountQueryName](searchValue)
      )
      // Map valid account query name to account query.
      .map((accountQueryName) => {
        if (accountQueryName === AccountQueryName.FULL_NAME) {
          return [
            {
              name: AccountQueryName.FIRST_NAME,
              value: getFirstName(searchValue),
            },
            {
              name: AccountQueryName.LAST_NAME,
              value: getLastName(searchValue),
            },
          ];
        }

        return [{ name: accountQueryName, value: searchValue }];
      })
  );
}

export const searchCompCardsLogic = createLogic({
  type: SEARCH_COMP_CARDS,
  latest: true,

  async process({ action, getState }, dispatch, done) {
    const searchValue = action.payload.value;
    const handleRoute = action.payload?.options?.handleCardRoute; // history

    const validAccountQueriesList = getValidAccountQueriesList(searchValue, [
      AccountQueryName.CARD_NUMBER,
    ]);

    if (validAccountQueriesList.length === 0) {
      dispatch(
        searchCompCardsRejected({
          validationError: new Error('Invalid search value.'),
        })
      );
      done();
      return;
    }

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;

    try {
      const cardResponse = await queryAccounts(
        posContext,
        pxContext,
        validAccountQueriesList[0]
      );

      // 1 card only please
      if (cardResponse?.customers.length === 0) {
        dispatch(searchCompCardsFulfilled([]));
        dispatch(
          searchCompCardsRejected({
            validationError: new Error(
              'No comp card found.  Card number must be an exact match.'
            ),
          })
        );
        done();
        return;
      }

      const order = await orderApi.retrieve();
      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        order,
        { referenceId: searchValue }
      );

      const cardSpecificAppliedDiscounts = response.appliedDiscounts?.filter(
        (disc) => disc?.metadata?.pxCompCardId === searchValue
      );

      dispatch(
        fetchCompDiscountsFulfilled(
          response.availableDiscounts,
          cardSpecificAppliedDiscounts,
          response.discountsNotApplied,
          response.unavailableDiscounts,
          response.updatedDiscounts
        )
      );

      handleRoute();
      dispatch(searchCompCardsFulfilled(cardResponse?.customers?.[0]));
    } catch (error) {
      dispatch(searchCompCardsRejected({ validationError: error }));
    } finally {
      done();
    }
  },
});

export const syncAppliedCompCardsLogic = createLogic({
  type: SYNC_APPLIED_COMP_CARD,
  latest: true,

  async process({ action, getState }, dispatch, done) {
    const appliedCardList = getState().searchCompCards?.appliedCompCards;
    const cardToSync = action.payload?.compCard;
    const cardIndex = appliedCardList.findIndex(
      (card) => card.cardNumber === action.payload?.compCard?.referenceId
    );
    const hasAppliedDiscounts =
      action.payload?.appliedCompDiscounts?.length >= 1;
    const isCardAlreadyApplied = cardIndex >= 0;
    let customer = undefined;
    let isSaleCustomer = false;

    function syncCardAsCustomerToCheck() {
      const postUpdateCardList = getState().searchCompCards?.appliedCompCards;

      if (postUpdateCardList?.length > 0) {
        // If there are available comp cards
        // Create the data for using CustomerConverter
        const rowFields = Object.values(cardToSync);
        rowFields[rowFields.length - 1] = postUpdateCardList[0].cardNumber;
        const headerFields = Array.from(CustomerConverter.CUSTOMER_MAP.keys());
        headerFields.unshift(
          Array.from(CustomerConverter.ADDRESS_MAP.keys())[0]
        );

        const mappedCustomer = CustomerConverter.convert(
          headerFields,
          Object.values(rowFields)
        );

        // Push mapped appliedCompCard to check
        dispatch(customerActions.addCustomerToSale(mappedCustomer));
      }
    }

    try {
      if (!isCardAlreadyApplied && hasAppliedDiscounts) {
        dispatch(
          addAppliedCompCard(
            cardToSync?.referenceId,
            action.payload?.appliedCompDiscounts
          )
        );
      } else if (isCardAlreadyApplied && !hasAppliedDiscounts) {
        dispatch(deleteAppliedCompCard(cardToSync.referenceId));
        customer = await safeGetCustomer();
        isSaleCustomer = cardToSync?.referenceId === customer?.referenceId;
        if (isSaleCustomer) {
          dispatch(customerActions.removeCustomerFromSale());
          syncCardAsCustomerToCheck();
        }
      } else if (isCardAlreadyApplied && hasAppliedDiscounts) {
        dispatch(
          updateAppliedCompCard(
            cardToSync?.referenceId,
            action.payload?.appliedCompDiscounts
          )
        );
      }
    } catch (error) {
      dispatch(addCompDiscountRejected(error));
    } finally {
      done();
    }
  },
});
